import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import { useAppState } from './AppStateContext';
import { clearCachedData } from '../lib/haxios';
import { triggerLogout } from '../hooks/useAuth';
import { ChevD, Search, Flag, Msg, Logout } from '../design/Icons';
import haxios from '../lib/haxios';
import { useNotificationsUnread } from '../hooks/useNotificationsUnread';
import { useUnreadMessages } from '../hooks/useUnreadMessages';

// The top context bar: brand, branch + operator + date + mode selectors,
// global search, notifications, user avatar, logout.
//
// Selectors read/write AppStateContext. Branches/operators are loaded lazily
// from the main API the first time the bar mounts after auth. If the fetches
// fail (dev, offline) we fall back to a minimal mock list so the UI stays usable.

const FALLBACK_BRANCHES = [
  { code: 'ALL', name: 'All branches' },
];

const FALLBACK_OPERATORS = [];

const initialsFrom = (name) => {
  if (!name) return '-';
  return name.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
};

const ContextBar = () => {
  const nav = useNavigate();
  const { uinfo, cinfo, apir, setCinfo, setUinfo, setApir, setLoginError, setIsLoggedIn } =
    useContext(GlobalContext);
  const { branch, operator, date, mode, setBranch, setOperator, setDate, setMode } = useAppState();
  const [branches, setBranches] = useState(FALLBACK_BRANCHES);
  const [operators, setOperators] = useState(FALLBACK_OPERATORS);
  const unread = useNotificationsUnread();
  // Global unread-messages count — spans every thread the user is in, including
  // ones for customers that never appear on their scoped lists.
  const { total: unreadMsgs } = useUnreadMessages();

  // Who can switch what:
  //  - admin: any branch, any operator (both selectors).
  //  - bm:    locked to their own branch, but free to pick operators in it
  //           (operator selector only).
  //  - everyone else: pinned to their own branch + initials (no selectors).
  const isAdmin = uinfo?.Status === 'admin';
  const isBM = uinfo?.Status === 'bm';

  useEffect(() => {
    if (!apir) return;
    let dead = false;
    (async () => {
      try {
        // See API_CONTRACTS.md § branches — MamCode is the code, Branch the name.
        const rows = await haxios.get('/salesassist/cplusassist/branches');
        if (dead) return;
        const list = Array.isArray(rows) ? rows : [];
        const seen = new Set(['ALL']);
        const cleaned = [{ code: 'ALL', name: 'All branches' }];
        for (const b of list) {
          const code = (b.MamCode || '').trim();
          const name = b.Branch || code;
          if (!code || code === 'NONE' || seen.has(code)) continue;
          if (b.Active === 0 || b.Active === false) continue; // hide retired branches
          seen.add(code);
          cleaned.push({ code, name });
        }
        setBranches(cleaned);
      } catch { /* keep fallback */ }

      try {
        const ops = await haxios.get('/salesassist/cplusassist/getOperators');
        if (dead) return;
        const list = Array.isArray(ops) ? ops : [];
        const seen = new Set();
        const cleaned = [];
        for (const o of list) {
          const inits = o.Inits || o.inits;
          if (!inits || seen.has(inits)) continue;
          seen.add(inits);
          cleaned.push({ inits, name: o.Name || o.name, branch: o.Branch || o.branch });
        }
        setOperators(cleaned);
      } catch { /* keep fallback */ }
    })();
    return () => { dead = true; };
  }, [apir]);

  // Non-admins can't roam freely. Force AppState to match uinfo so a persisted
  // value (or one an admin left behind on a shared machine) can't leak the
  // wrong scope. Branch must be a known MamCode — Operators.Branch can hold a
  // display name (API_CONTRACTS "Branch field gotcha"), so fall back to the
  // all-branches aggregate when it isn't.
  useEffect(() => {
    if (!uinfo || isAdmin) return;

    const known = branches.some(b => b.code === uinfo.Branch);
    const myBranch = known ? uinfo.Branch : 'ALL';
    if (branch !== myBranch) setBranch(myBranch);

    if (isBM) {
      // BMs may pick any operator in their branch; only reset a stray
      // selection that belongs to a different branch.
      const validOp = operator === 'ALL'
        || operators.some(o => o.inits === operator && o.branch === myBranch);
      if (!validOp && operators.length) setOperator('ALL');
    } else {
      // Reps & co. are pinned to their own initials.
      const myOperator = uinfo.Inits || 'ALL';
      if (operator !== myOperator) setOperator(myOperator);
    }
  }, [uinfo, isAdmin, isBM, branches, operators, operator, branch, setOperator, setBranch]);

  const handleLogout = () => {
    if (!window.confirm('Sign out of Sales Assist?')) return;
    clearCachedData();
    setCinfo(null);
    setUinfo(null);
    setApir('');
    setLoginError('');
    setIsLoggedIn(false);
    localStorage.setItem('logoutTimestamp', Date.now().toString());
    triggerLogout();
    nav('/login');
  };

  const displayName = uinfo?.Name || uinfo?.Username || cinfo?.Username || '-';
  const avatarText = uinfo?.Inits || initialsFrom(displayName);
  const roleLabel = [uinfo?.Position, uinfo?.Branch].filter(Boolean).join(' · ') || (uinfo?.Status || '');

  return (
    <div className="ctxbar">
      <div className="ctx-left">
        <img src="/automotivemation-wide.png" alt="Automotivemation" className="ctx-wordmark"/>
        <span className="ctx-divider"/>
        <div className="ctx-crumb">
          <span className="label">Sales Assist</span>
        </div>
      </div>

      <div className="ctx-mid">
        {isAdmin && (
          <label className="ctx-field">
            <span className="label">Branch</span>
            <div className="ctx-select">
              <select value={branch} onChange={e => setBranch(e.target.value)}>
                {branches.map(b => (
                  <option key={b.code} value={b.code}>{b.code} · {b.name}</option>
                ))}
              </select>
              <ChevD size={12} />
            </div>
          </label>
        )}

        {(isAdmin || isBM) && (
          <label className="ctx-field">
            <span className="label">Operator</span>
            <div className="ctx-select">
              <select value={operator} onChange={e => setOperator(e.target.value)}>
                <option value="ALL">All operators</option>
                {(branch === 'ALL' ? operators : operators.filter(o => o.branch === branch)).map(o => (
                  <option key={o.inits} value={o.inits}>{o.inits} · {o.name}</option>
                ))}
              </select>
              <ChevD size={12} />
            </div>
          </label>
        )}

        <label className="ctx-field">
          <span className="label">Date</span>
          <div className="ctx-seg">
            {['today','yesterday','week','month'].map(x => (
              <button
                key={x}
                type="button"
                className={`seg ${date === x ? 'on' : ''}`}
                onClick={() => setDate(x)}
              >
                {x}
              </button>
            ))}
          </div>
        </label>

        <label className="ctx-field">
          <span className="label">View</span>
          <div className="ctx-seg">
            {['gross','net'].map(x => (
              <button
                key={x}
                type="button"
                className={`seg ${mode === x ? 'on' : ''}`}
                onClick={() => setMode(x)}
              >
                {x}
              </button>
            ))}
          </div>
        </label>
      </div>

      <div className="ctx-right">
        <button
          type="button"
          className="ctx-search"
          onClick={() => {
            const evt = new KeyboardEvent('keydown', {
              key: 'k',
              metaKey: /Mac/.test(navigator.platform),
              ctrlKey: !/Mac/.test(navigator.platform),
              bubbles: true,
            });
            window.dispatchEvent(evt);
          }}
          style={{ cursor: 'pointer', border: 'none' }}
          title="Open command palette"
        >
          <Search size={13} />
          <span>Search customers, accounts, reps…</span>
          <kbd>⌘K</kbd>
        </button>
        <button
          type="button"
          className="btn ghost sm"
          title={unreadMsgs > 0 ? `${unreadMsgs} unread message${unreadMsgs === 1 ? '' : 's'}` : 'Messages'}
          onClick={() => nav('/notifications')}
          style={{ position: 'relative' }}
          aria-label="Messages"
        >
          <Msg size={13}/>
          {unreadMsgs > 0 && (
            <span
              aria-label={`${unreadMsgs} unread messages`}
              style={{
                position: 'absolute',
                top: -4, right: -4,
                minWidth: 16, height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--orange)',
                color: '#fff',
                font: '600 10px/16px var(--font-ui)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
              className="num"
            >
              {unreadMsgs > 99 ? '99+' : unreadMsgs}
            </span>
          )}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          title={unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}
          onClick={() => nav('/notifications')}
          style={{ position: 'relative' }}
        >
          <Flag size={13}/>
          {unread > 0 && (
            <span
              aria-label={`${unread} unread`}
              style={{
                position: 'absolute',
                top: -4, right: -4,
                minWidth: 16, height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--accent)',
                color: 'var(--paper)',
                font: '600 10px/16px var(--font-ui)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
              className="num"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
        <div className="ctx-user">
          <div className="avatar">{avatarText}</div>
          <div className="ctx-user-meta">
            <div className="ctx-user-name">{displayName}</div>
            <div className="label">{roleLabel}</div>
          </div>
          <button
            type="button"
            className="ctx-logout"
            title="Sign out"
            aria-label="Sign out"
            onClick={handleLogout}
          >
            <Logout size={13}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContextBar;
